import React, { useState, useEffect, useCallback } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import {
  RevisionExcerpt,
  Revision,
  fetchRevisionsExcerpt,
  fetchRevision,
  revertToRevision
} from '../../../../services/api';
import { FestivalData } from '../../../../model/FestivalData';
import './VersionHistory.css';
import Modal from '../../../../components/Modal';
import PageHeader from '../../../../components/PageHeader';
import { describeRevisionChanges } from './revisionChanges';
import { currentMessages, useLanguage, useMessages } from '../../../../i18n/I18n';
import LoadingPlaceholder from '../../../../components/LoadingPlaceholder';
import { loadTabData } from '../../../../services/tabData';

interface VersionHistoryProps {
  sessionToken: string;
  festivalId: string;
  versionId: string;
  isPublished: boolean;
  onRevert: (savedData: FestivalData) => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  sessionToken,
  festivalId,
  versionId,
  isPublished,
  onRevert,
}) => {
  const t = useMessages('history');
  const common = useMessages('common');
  const { locale } = useLanguage();
  const [revisions, setRevisions] = useState<RevisionExcerpt[]>([]);
  const [selectedRevision, setSelectedRevision] = useState<RevisionExcerpt | null>(null);
  const [fullRevision, setFullRevision] = useState<Revision | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDiff, setIsLoadingDiff] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  const loadRevisions = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const fetchedRevisions = await loadTabData(
        festivalId, `revisions/${versionId}`, () => fetchRevisionsExcerpt(sessionToken, festivalId, versionId),
      );
      setRevisions(fetchedRevisions);
    } catch (err) {
      console.error('Error fetching revisions:', err);
      setLoadError(err instanceof Error ? err.message : currentMessages('history').loadFailed);
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken, festivalId, versionId]);

  useEffect(() => {
    loadRevisions();
  }, [loadRevisions]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleSeeChanges = async (revision: RevisionExcerpt) => {
    setSelectedRevision(revision);
    setFullRevision(null);
    setDiffError(null);
    setIsLoadingDiff(true);

    try {
      const fullRevisionData = await fetchRevision(
        sessionToken,
        festivalId,
        revision.uuid
      );
      setFullRevision(fullRevisionData);
    } catch (err) {
      console.error('Error fetching revision details:', err);
      setDiffError(err instanceof Error ? err.message : t.detailsFailed);
    } finally {
      setIsLoadingDiff(false);
    }
  };

  const closeModals = () => {
    setSelectedRevision(null);
    setFullRevision(null);
    setShowConfirmModal(false);
    setRevertError(null);
  };

  const handleRevertClick = () => {
    setRevertError(null);
    setShowConfirmModal(true);
  };

  const handleConfirmRevert = async () => {
    if (!selectedRevision) return;

    try {
      setIsReverting(true);
      setRevertError(null);
      const savedData = await revertToRevision(
        sessionToken,
        festivalId,
        versionId,
        selectedRevision.uuid
      );
      onRevert(savedData);
      closeModals();
      loadRevisions();
    } catch (err) {
      console.error('Error reverting to revision:', err);
      setRevertError(err instanceof Error ? err.message : t.revertFailed);
    } finally {
      setIsReverting(false);
    }
  };

  const renderChanges = (revision: Revision): React.ReactElement => {
    if (!revision.before || !revision.after) {
      return (
        <>
          <div className="diff-error">
            {t.cannotRebuild}
          </div>
          <pre className="diff-raw">{revision.diff}</pre>
        </>
      );
    }
    const changes = describeRevisionChanges(revision.before, revision.after);
    if (changes.length === 0) {
      return <div className="diff-empty">{t.nothingChanged}</div>;
    }
    return (
      <ul className="revision-changes">
        {changes.map((change, index) => (
          <li key={index} className={`revision-change ${change.kind}`}>
            <div className="revision-change-head">
              <span className={`revision-change-kind ${change.kind}`}>{t.kinds[change.kind]}</span>
              <span className="revision-change-label">{change.label}</span>
            </div>
            {change.kind === 'changed' && (
              <div className="revision-change-values">
                <div className="revision-change-value before">
                  <span className="revision-change-caption">{t.before}</span>
                  <span className="revision-change-text">{change.previousValue}</span>
                </div>
                <div className="revision-change-value after">
                  <span className="revision-change-caption">{t.after}</span>
                  <span className="revision-change-text">{change.newValue}</span>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  // Revisions are listed from the latest, so every revision above the selected one gets undone too
  const laterRevisionCount = selectedRevision
    ? revisions.findIndex(revision => revision.uuid === selectedRevision.uuid)
    : 0;

  return (
    <div className="version-history">
      <PageHeader
        title={t.title}
        description={t.description(versionId)}
      />
      {isLoading ? (
        <LoadingPlaceholder layout="rows" />
      ) : loadError ? (
        <div className="history-error">{loadError}</div>
      ) : revisions.length === 0 ? (
        <div className="empty-state">
          <p>{t.empty}</p>
        </div>
      ) : (
        <>
          <ol className="revision-list">
            {revisions.map((revision) => (
              <li key={revision.uuid} className="revision-item">
                <span className="revision-marker" aria-hidden="true" />
                <div className="revision-row">
                  <div className="revision-user-info">
                    <span className="user-email">{revision.user_email}</span>
                    <span className="revision-date">{formatDate(revision.date)}</span>
                  </div>
                  <button type="button" onClick={() => handleSeeChanges(revision)} className="btn">
                    {t.seeChanges}
                  </button>
                </div>
              </li>
            ))}
          </ol>

          <Modal
            className="history-dialog"
            isOpen={selectedRevision !== null && !showConfirmModal}
            onClose={closeModals}
          >
            <h3>{t.changesSaved}</h3>
            {selectedRevision && (
              <p className="revision-modal-subtitle">
                {t.savedBy(selectedRevision.user_email, formatDate(selectedRevision.date))}
              </p>
            )}
            <div className="diff-content">
              {isLoadingDiff ? (
                <LoadingPlaceholder layout="table" count={3} />
              ) : fullRevision ? (
                renderChanges(fullRevision)
              ) : (
                <div className="diff-error">{diffError ?? t.detailsFailed}</div>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={handleRevertClick}
                disabled={!fullRevision?.before}
              >
                {t.revertToBefore}
              </button>
              <button type="button" onClick={closeModals}>
                {common.close}
              </button>
            </div>
          </Modal>

          <Modal
            className="history-dialog history-confirm"
            isOpen={showConfirmModal}
            onClose={() => !isReverting && closeModals()}
          >
            <h3>{t.confirmTitle}</h3>
            <p>
              {t.willBeSaved(versionId)}
              {laterRevisionCount > 0 && t.alsoUndoes(laterRevisionCount)}
              .
            </p>
            <p className={isPublished ? 'history-published' : 'history-draft'}>
              {isPublished && <FiAlertTriangle aria-hidden="true" />}
              <span>
                {isPublished ? t.publishedWarning : t.staysDraft}
                {' '}{t.canUndo}
              </span>
            </p>
            {revertError && <div className="diff-error">{revertError}</div>}
            <div className="modal-actions">
              <button
                type="button"
                onClick={handleConfirmRevert}
                className={isPublished ? 'danger' : undefined}
                disabled={isReverting}
              >
                {isReverting ? t.reverting : t.revert}
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={isReverting}
              >
                {common.cancel}
              </button>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default VersionHistory;
